import { useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChatService, SendMessageResult } from '../services/chatService';
import { ConciergeSendMessageRequest } from '../models/conciergeContracts';

// Query Keys
export const chatKeys = {
   all: ['chat'] as const,
   history: () => [...chatKeys.all, 'history'] as const,
   session: () => [...chatKeys.all, 'session'] as const
};

/**
 * Hook to fetch chat history
 */
export function useChatHistoryQuery(enabled: boolean = true) {
   return useQuery({
      queryKey: chatKeys.history(),
      queryFn: async () => {
         return await ChatService.getChatHistory();
      },
      staleTime: 1000 * 60 * 5, // 5 minutes
      enabled: enabled // Only fetch when enabled
   });
}

/**
 * Returns a function that refetches the history into the query cache (the
 * `useChatInitialization` effect reconciles it into the message list).
 *
 * Calls that arrive while a recovery fetch is running are coalesced into one
 * more fetch after it, so the last trigger (e.g. a reconnect) always gets a
 * snapshot taken after it fired. Resolves to whether the last fetch succeeded.
 */
export function useChatHistoryRecovery(enabled: boolean) {
   const queryClient = useQueryClient();
   const inFlight = useRef<Promise<boolean> | null>(null);
   const again = useRef(false);
   const enabledRef = useRef(enabled);
   enabledRef.current = enabled;

   return useCallback((): Promise<boolean> => {
      if (!enabledRef.current) return Promise.resolve(false);
      if (inFlight.current) {
         again.current = true;
         return inFlight.current;
      }

      const run = async (): Promise<boolean> => {
         let ok = false;
         do {
            again.current = false;
            try {
               await queryClient.fetchQuery({
                  queryKey: chatKeys.history(),
                  queryFn: () => ChatService.getChatHistory({ silent: true }),
                  staleTime: 0
               });
               ok = true;
            } catch (error) {
               console.warn('[chat] history recovery failed', error);
               ok = false;
               break; // the next reconnect / visibility change will try again
            }
         } while (again.current);
         return ok;
      };

      const promise = run().finally(() => {
         inFlight.current = null;
      });
      inFlight.current = promise;
      return promise;
   }, [queryClient]);
}

/**
 * Hook to send a message (one turn, identified by `clientTurnId`)
 */
export function useSendMessageMutation() {
   const queryClient = useQueryClient();

   return useMutation<SendMessageResult, Error, ConciergeSendMessageRequest>({
      mutationFn: (data) => ChatService.sendMessage(data),
      onSuccess: (result) => {
         // Pull the server copy of the user message (and the answer, if ready) into history.
         if (result.status === 'sent') {
            queryClient.invalidateQueries({ queryKey: chatKeys.history() });
         }
      }
   });
}
